import { Box, Split, Stack } from '@dropbox/dig-foundations';
import { UIIcon } from '@dropbox/dig-icons';
import {
  EditLine,
  FileLine,
  UploadFileLine,
  UploadLine,
} from '@dropbox/dig-icons/dist/mjs/assets';
import { Text } from '@dropbox/dig-components/typography';
import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import styles from './attachments-field.module.css';
import { Button } from '@dropbox/dig-components/buttons';
import { isAttachmentsDrawerOpen } from 'signer-app/parts/attachments-drawer/attachments-drawer';
import { useAtom, useAtomValue } from 'jotai';
import { accessibleViewAtom } from 'signer-app/signer-signature-document/accessible-document-toggle';
import { useAttachmentsInfo } from 'signer-app/utils/use-attachments-info';
import { FormLabel, FormRow } from '@dropbox/dig-components/form_row';
import { FieldHelperText } from './field-helper-text';
import { messages } from '../messages';
import { useBundleProps } from 'signer-app/bundle-props';

const ATTACHMENTS_FIELD_ID = 'field_attachments';

interface NoUploadedAttachmentsButtonProps {
  attachmentsNumber: number;
}

function NoUploadedAttachmentsButton({
  attachmentsNumber,
}: NoUploadedAttachmentsButtonProps) {
  const [, setIsOpen] = useAtom(isAttachmentsDrawerOpen);
  const isUsingFormView = useAtomValue(accessibleViewAtom);
  const { hasCustomStyles } = useBundleProps();

  if (isUsingFormView) {
    return (
      <Button
        variant={hasCustomStyles ? 'outline' : 'opacity'}
        onClick={() => setIsOpen(true)}
        withIconStart={<UIIcon src={UploadLine} />}
        size="large"
        fullWidth
        className={styles.uploadButton}
        id={ATTACHMENTS_FIELD_ID}
      >
        <FormattedMessage
          id="4ec49f88fe49919d81d2fd2cce0d560fe44a057504d3b79e8c2b7c81d1203b9c"
          defaultMessage="Upload attachments"
          description="Text in upload attachments button in form view"
        />
      </Button>
    );
  }

  return (
    <Box
      as="button"
      role="button"
      display="flex"
      alignItems="center"
      flexDirection="column"
      backgroundColor="transparent"
      outline="none"
      className={styles.attachmentsButton}
      paddingX="68"
      paddingY="16"
      width="100%"
      onClick={() => setIsOpen(true)}
      cursor="pointer"
      id={ATTACHMENTS_FIELD_ID}
    >
      <Text color="subtle">
        <UIIcon src={UploadFileLine} width="37" height="37" />
      </Text>
      <Text size="medium" color="subtle">
        <FormattedMessage
          id="0ba851686107e3eb7a7df94129a15181ed1f794a9c899f9f6057bbc12eb9a791"
          defaultMessage="Click here to upload { attachmentsNumber, plural, =1 {attachment} other {attachments} }"
          description="Text in upload attachments button"
          values={{ attachmentsNumber }}
        />
      </Text>
    </Box>
  );
}

interface UploadedAttachmentsButtonProps {
  uploadedAttachmentsNumber: number;
  requiredAttachmentsNumber: number;
  optionalAttachmentsNumber: number;
  optionalAttachmentsUploaded: number;
}

function UploadedAttachmentsButton({
  requiredAttachmentsNumber,
  uploadedAttachmentsNumber,
  optionalAttachmentsNumber,
  optionalAttachmentsUploaded,
}: UploadedAttachmentsButtonProps) {
  const [, setIsOpen] = useAtom(isAttachmentsDrawerOpen);
  const { hasCustomStyles } = useBundleProps();
  return (
    <Split alignY="center">
      <Split.Item>
        <Box display="flex" alignItems="center">
          <UIIcon src={FileLine} />
        </Box>
      </Split.Item>
      <Split.Item width="fill">
        <Text size="medium" isBold>
          {requiredAttachmentsNumber > 0 ? (
            <FormattedMessage
              id="4a24aeee861671948b0cb24ff2e22c4b5ca6180faf89ea5478db636824b54f31"
              defaultMessage="{ uploadedAttachmentsNumber }/{ requiredAttachmentsNumber } required uploaded"
              description="Text in uploaded attachments button"
              values={{ uploadedAttachmentsNumber, requiredAttachmentsNumber }}
            />
          ) : (
            <FormattedMessage
              id="1479699d32ffe6d63da92427159ae8809c90153a65b30addd5e6ff9591c59c00"
              defaultMessage="{ optionalAttachmentsUploaded }/{ optionalAttachmentsNumber } optional uploaded"
              description="Text in uploaded attachments button"
              values={{
                optionalAttachmentsUploaded,
                optionalAttachmentsNumber,
              }}
            />
          )}
        </Text>
      </Split.Item>
      <Split.Item>
        <Button
          variant={hasCustomStyles ? 'outline' : 'opacity'}
          onClick={() => setIsOpen(true)}
          id={ATTACHMENTS_FIELD_ID}
        >
          <UIIcon src={EditLine} />
        </Button>
      </Split.Item>
    </Split>
  );
}

const validationMessages = defineMessages({
  requiredAttachmentsMessage: {
    id: 'error.validation.requiredAttachmentsMessage',
    description:
      'error message for attachments field in signer flow, shows when required attachments are not uploaded',
    defaultMessage: 'Please upload all required attachments',
  },
});

// More of a pseudo field
export function AttachmentsField() {
  const {
    requiredAttachmentsNumber,
    requiredAttachmentsUploaded,
    optionalAttachmentsNumber,
    optionalAttachmentsUploaded,
  } = useAttachmentsInfo();
  const intl = useIntl();

  const errorMessage = React.useMemo(() => {
    if (
      requiredAttachmentsNumber > 0 &&
      requiredAttachmentsUploaded !== 0 &&
      requiredAttachmentsUploaded !== requiredAttachmentsNumber
    ) {
      return intl.formatMessage(validationMessages.requiredAttachmentsMessage);
    }

    return undefined;
  }, [requiredAttachmentsNumber, requiredAttachmentsUploaded, intl]);

  return (
    <FormRow>
      <FormLabel
        subtext={
          requiredAttachmentsNumber > 0
            ? undefined
            : intl.formatMessage(messages.optional)
        }
        htmlFor={ATTACHMENTS_FIELD_ID}
      >
        <FormattedMessage
          id="signer.attachment.drawer.field.label"
          defaultMessage={'Attachments'}
          description="Label for attachments field in drawer"
        />
      </FormLabel>
      <Stack gap="8">
        <Text color="subtle">
          {requiredAttachmentsNumber > 0 && optionalAttachmentsNumber > 0 && (
            <FormattedMessage
              id="e7da0c23a7dc3e066d6e1bd6aa68b9111a802aaba1856239c1e0f6c372e0b91a"
              defaultMessage="{requiredAttachmentsNumber, number} {requiredAttachmentsNumber, plural, =1 {required} other {required}} and {optionalAttachmentsNumber, number} {optionalAttachmentsNumber, plural, =1 {optional attachment} other {optional attachments}} requested."
              description="Text in signer flow of attachment field, informing signer that sender has requested documents to be attached with the signature request"
              values={{
                requiredAttachmentsNumber,
                optionalAttachmentsNumber,
              }}
            />
          )}
          {requiredAttachmentsNumber > 0 && optionalAttachmentsNumber === 0 && (
            <FormattedMessage
              id="6b8123027eab8469a2e47bf7b3d9dc03249a2e024800e124e35183a09980d128"
              defaultMessage="{requiredAttachmentsNumber, number} {requiredAttachmentsNumber, plural, =1 {required attachment} other {required attachments}} requested."
              description="Text in signer flow of attachment field, informing signer that sender has requested documents to be attached with the signature request"
              values={{
                requiredAttachmentsNumber,
              }}
            />
          )}
          {requiredAttachmentsNumber === 0 && optionalAttachmentsNumber > 0 && (
            <FormattedMessage
              id="0a041dbc662dfa517bd0ede6ab2b4fdc294a273aac575fc1a2abc4a418ec2ae6"
              defaultMessage="{optionalAttachmentsNumber, number} {optionalAttachmentsNumber, plural, =1 {optional attachment} other {optional attachments}} requested."
              description="Text in signer flow of attachment field, informing signer that sender has requested documents to be attached with the signature request"
              values={{
                optionalAttachmentsNumber,
              }}
            />
          )}
        </Text>
        {requiredAttachmentsUploaded > 0 || optionalAttachmentsUploaded > 0 ? (
          <UploadedAttachmentsButton
            requiredAttachmentsNumber={requiredAttachmentsNumber}
            uploadedAttachmentsNumber={requiredAttachmentsUploaded}
            optionalAttachmentsNumber={optionalAttachmentsNumber}
            optionalAttachmentsUploaded={optionalAttachmentsUploaded}
          />
        ) : (
          <NoUploadedAttachmentsButton
            attachmentsNumber={requiredAttachmentsNumber}
          />
        )}
      </Stack>
      <FieldHelperText
        id="attachments"
        required={!!requiredAttachmentsNumber}
        errorMessage={errorMessage}
      />
    </FormRow>
  );
}
